import { redirect, RouteObject } from 'react-router';

import { page as lazyPage, type PageImporter } from '@router/adminPage';
import { AdminSectionLayout } from '@router/sectionLayout';
import { createRouteGuardLoader } from '@router/guards';
import type { RouteHandle } from '@router/routeHandle';
import { defaultLanguage } from '@services/defaultLanguage';

const ANY_ROLE = ['admin', 'customerservice', 'ftl', 'va-employee', 'medicadmin', 'it'];

const ADMIN: RouteHandle = { area: 'admin' };

const pageModules = import.meta.glob(['../../pages/*/*.tsx', '!../../pages/*/*.spec.tsx']) as Record<
  string,
  PageImporter
>;

const page = (
  id: string,
  path: string,
  name: string,
  handle: RouteHandle = ADMIN,
  children?: RouteObject[],
): RouteObject => ({
  id,
  path,
  handle,
  loader: createRouteGuardLoader(id, handle),
  lazy: lazyPage(pageModules[`../../pages/${name}/${name}.tsx`], name),
  ...(children && { children }),
});

const siblingSection = (id: string, path: string, handle: RouteHandle, children: RouteObject[]): RouteObject => ({
  id,
  path,
  handle,
  loader: createRouteGuardLoader(id, handle),
  Component: AdminSectionLayout,
  children,
});

const urlOnly = (id: string, path: string, handle: RouteHandle = ADMIN): RouteObject => ({
  id,
  path,
  handle,
  loader: createRouteGuardLoader(id, handle),
  Component: () => null,
});

const pharmaciesHandle: RouteHandle = { ...ADMIN, auth: ['admin'] };
const patientsHandle: RouteHandle = {
  ...ADMIN,
  auth: ['admin', 'customerservice'],
};
const doctorsHandle: RouteHandle = {
  ...ADMIN,
  auth: ['customerservice', 'admin', 'medicadmin'],
};
const openRequestsHandle: RouteHandle = {
  ...ADMIN,
  auth: ['admin', 'customerservice'],
};
const failedRequestsHandle: RouteHandle = { ...ADMIN, auth: ['admin'] };
const promotionsHandle: RouteHandle = {
  ...ADMIN,
  auth: ['admin', 'customerservice'],
};

const routes: RouteObject[] = [
  {
    id: 'Root',
    path: '/',
    loader: () => {
      const lastPath = localStorage.getItem('pwa-lastpath');
      if (lastPath) {
        try {
          const parsed = JSON.parse(lastPath) as { path?: string };
          if (parsed?.path) return redirect(parsed.path);
        } catch {
          // fall through to the default landing route
        }
      }
      return redirect(`/${defaultLanguage.get()}/login`);
    },
    Component: () => null,
  },
  {
    id: 'Admin',
    path: ':locale',
    loader: ({ params }) => redirect(`/${params.locale ?? defaultLanguage.get()}/login`),
    Component: () => null,
  },

  page('Admin Login', ':locale/login', 'Login', {
    ...ADMIN,
    forceAnonymousHeader: true,
  }),
  page('Admin Login Breakglass', ':locale/login-breakglass', 'LoginBreakglass', {
    ...ADMIN,
    forceAnonymousHeader: true,
  }),
  page('Admin Welcome', ':locale/welcome', 'Welcome', {
    ...ADMIN,
    auth: ANY_ROLE,
  }),
  page('Admin Profile', ':locale/profile', 'Profile', {
    ...ADMIN,
    auth: ANY_ROLE,
  }),

  page('Admin PickPost', ':locale/pickpost', 'PickPost', {
    ...ADMIN,
    auth: ['admin', 'ftl'],
  }),
  page('Admin PickPost View', ':locale/pickpost-view', 'PickPostView', {
    ...ADMIN,
    auth: ['admin', 'ftl', 'customerservice', 'va-employee'],
  }),

  page('Admin Pharmacies', ':locale/pharmacies', 'PharmaciesLayout', pharmaciesHandle, [
    urlOnly('Pharmacies List', 'list', pharmaciesHandle),
    urlOnly('Pharmacies Detail', 'detail/:pharmacyId', pharmaciesHandle),
    urlOnly('Pharmacies Create', 'create', pharmaciesHandle),
  ]),

  page('Admin Patient', ':locale/patients', 'PatientsLayout', patientsHandle, [
    urlOnly('Patient List', 'list/:page', patientsHandle),
    urlOnly('Patient Detail', 'detail/:patientId', patientsHandle),
  ]),

  page('Admin Doctor', ':locale/docs', 'DoctorsLayout', doctorsHandle, [
    urlOnly('Doctor List', 'list/:page', doctorsHandle),
    urlOnly('Doctor Detail', 'detail/:docId', doctorsHandle),
  ]),

  siblingSection('Open Requests', ':locale/open-requests', openRequestsHandle, [
    page('Open Requests List', 'list/:page?', 'OpenRequestsList', openRequestsHandle),
    page('Open Requests Detail', 'detail/:requestId', 'OpenRequestsDetail', openRequestsHandle),
  ]),

  siblingSection('Failed Requests', ':locale/failed-requests', failedRequestsHandle, [
    page('Failed Requests List', 'list/:page?', 'FailedRequestsList', failedRequestsHandle),
    page('Failed Requests Detail', 'detail/:requestId', 'FailedRequestsDetail', failedRequestsHandle),
  ]),

  page('Admin Promotions', ':locale/promotions', 'PromotionsLayout', promotionsHandle, [
    urlOnly('Admin Promotions List', 'list', promotionsHandle),
    urlOnly('Admin Promotions Detail', 'detail/:id?', promotionsHandle),
  ]),

  page('Admin Maintenance', ':locale/maintenance', 'Maintenance', {
    ...ADMIN,
    auth: ['admin', 'it'],
  }),

  page('Admin 401 Page', ':locale/not-authorized-401', 'HttpStatus401', { ...ADMIN, httpStatus: 401 }),
  page('404 Page', ':locale/not-found-404', 'HttpStatus404', {
    ...ADMIN,
    httpStatus: 404,
  }),

  {
    id: '404 Redirect',
    path: '*',
    loader: ({ request }) => {
      const url = new URL(request.url);
      const [firstSegment] = url.pathname.split('/').filter(Boolean);
      const locale = ['de', 'fr', 'it'].includes(firstSegment) ? firstSegment : defaultLanguage.get();
      return redirect(`/${locale}/not-found-404?redir=${encodeURIComponent(url.pathname + url.search)}`);
    },
    Component: () => null,
  },
];

export { routes };
